import { Box, useTheme, Typography, Stack, Collapse, TextField, FormHelperText } from '@mui/material';
import { ChevronDown, ChevronUp } from 'react-feather';
import { get } from 'lodash';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

//Components
import IconFeather from 'src/components/common/Icon';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import { CheckoutStep } from 'src/features/Checkout/config';
import CheckoutDone from 'src/asset/icons/checkout-done-step.svg';
import { ICheckoutForm } from 'src/utils/typings/dto/payment';

const BillingInformation = ({
  open,
  isStepDone,
  handleChangeStep,
}: {
  open: boolean;
  isStepDone: boolean;
  handleChangeStep: (step: CheckoutStep) => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const formik = useFormikContext<ICheckoutForm>();

  const getError = (type: string) => {
    return get(formik.touched, type) && Boolean(get(formik.errors, type));
  };

  const renderError = (type: string) => {
    return (
      get(formik.touched, type) &&
      get(formik.errors, type) && (
        <FormHelperText error id={'helper-text-billing-information-' + type}>
          {String(get(formik.errors, type))}
        </FormHelperText>
      )
    );
  };

  return (
    <Stack direction="column">
      <Stack direction="row" spacing={1.5} alignItems="center">
        {isStepDone ? (
          <Box borderRadius={999} bgcolor={theme.palette.primary.main} width={40} height={40} position="relative">
            <Box
              component={'img'}
              src={CheckoutDone}
              position="absolute"
              left="50%"
              top="50%"
              sx={{
                transform: 'translate(-50%, -50%)',
              }}
            />
          </Box>
        ) : (
          <Box borderRadius={999} border={`1px solid #000`} py={1} px={2}>
            <Typography variant="body1" component="span">
              2
            </Typography>
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flex={1}
          mb={3}
          sx={{ cursor: 'pointer' }}
          onClick={() => handleChangeStep(CheckoutStep.BillingInformation)}
        >
          <Typography variant="h6" color={isStepDone ? 'primary.main' : 'text.primary'}>
            {t('checkout.billing_information.title')}
          </Typography>
          <IconFeather icon={open ? <ChevronUp /> : <ChevronDown />} />
        </Stack>
      </Stack>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack direction="column" spacing={3} pl={7} mt={3}>
          <Stack direction="column" spacing={3}>
            <Stack direction="row" spacing={1.5}>
              <Box width="100%">
                <TextField
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(getError('first_name'))}
                  id="first_name"
                  name="first_name"
                  size="small"
                  placeholder={t('checkout.billing_information.first_name')}
                  label={t('checkout.billing_information.first_name')}
                  required
                  fullWidth
                />
                {renderError('first_name')}
              </Box>
              <Box width="100%">
                <TextField
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(getError('last_name'))}
                  id="last_name"
                  name="last_name"
                  size="small"
                  placeholder={t('checkout.billing_information.last_name')}
                  label={t('checkout.billing_information.last_name')}
                  required
                  fullWidth
                />
                {renderError('last_name')}
              </Box>
            </Stack>
            <Box>
              <TextField
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(getError('email'))}
                id="email"
                name="email"
                size="small"
                placeholder={t('checkout.billing_information.email_address')}
                label={t('checkout.billing_information.email_address')}
                required
                fullWidth
              />
              {renderError('email')}
            </Box>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <ButtonWithLoading
              variant="contained"
              size="large"
              disabled={!formik.isValid}
              onClick={() => handleChangeStep(CheckoutStep.PaymentMethod)}
            >
              {t('global.next')}
            </ButtonWithLoading>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default BillingInformation;
